import { Icon, Intent } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import axios from "axios";
import classNames from "classnames";
import { AuditingTable } from "components/auditing";
import { Button } from "components/buttons";
import { DataDisplayRecord } from "components/data-display";
import { GridLayout } from "components/grid";
import { ShowHide } from "components/layout";
import { Breadcrumbs, Tabs } from "components/navigation";
import { NotificationInline, NotificationToaster } from "components/notifications";
import { UserAccess, UserHasAccess } from "components/user-access";
import { PageRestricted } from "pages/errors/page-restricted";
import PropTypes from "prop-types";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { GreyFleetService } from 'services';
import { GreyFleetInsuranceAIResults } from "./GreyFleetInsuranceAIResults";
import { GreyFleetInsuranceDetails } from "./GreyFleetInsuranceDetails";
import { GreyFleetInsuranceDocument } from "./GreyFleetInsuranceDocument";
import { GreyFleetInsuranceInvalidationModal } from "./GreyFleetInsuranceInvalidationModal";
import "./GreyFleetInsuranceModal.css";
import { GreyFleetInsuranceValidationModal } from "./GreyFleetInsuranceValidationModal";
import { DateToLocal } from "components/dates";

export function GreyFleetInsuranceValidation(
    {
        breadcrumbs,
        requiredAction,
        title
    }
) {
    const noHistoryDataMessage = "No validation has been performed for the selected insurance.";

    const { id } = useParams();
    const canAccessPage = UserHasAccess(requiredAction);

    const [loading, setLoading] = useState(true);
    const [actionInProgress, setActionInProgress] = useState(false);
    const [tabs, setTabs] = useState([
        { key: "Details", title: "Details", selected: true },
        { key: "Documents", title: "Documents" },
        { key: "History", title: "History" }
    ]);
    const [currentTab, setCurrentTab] = useState(0);
    const [showValidationModal, setShowValidationModal] = useState(false);
    const [showInvalidationModal, setShowInvalidationModal] = useState(false);
    const [insuranceDetails, setInsuranceDetails] = useState(null);
    const [coverTypes, setCoverTypes] = useState([]);

    const [historyData, setHistoryData] = useState([]);
    const [historyRecordCount, setHistoryRecordCount] = useState(0);
    const [historyLoading, setHistoryLoading] = useState(false);
    
    const hasApprover = useMemo(() => {
        return insuranceDetails?.human.approver !== null && insuranceDetails?.human.approver !== "";
    }, [insuranceDetails]);

    const notificationText = useMemo(() => {
        const date = <DateToLocal key={`approval-date-${insuranceDetails?.checkId}`} format='DD/MM/YYYY HH:mm'>{insuranceDetails?.human?.approvalDate}</DateToLocal>;
        return <div>Insurance {insuranceDetails?.human.result ? "Validated" : "Invalidated"} on {date} by {insuranceDetails?.human.approver}.</div>;
    }, [insuranceDetails, hasApprover]);

    const humanResult = useMemo(() => {
        if (!hasApprover) {
            return <div>N/A</div>;
        }

        if (insuranceDetails?.human.result === true) {
            return (
                <div className="inline-items">
                    <Icon intent={Intent.SUCCESS} icon={IconNames.TickCircle} size={12} />
                    <span>Valid</span>
                </div>
            );
        } else {
            return (
                <div className="inline-items">
                    <Icon intent={Intent.DANGER} icon={IconNames.BanCircle} size={12} />
                    <span>Fail</span>
                </div>
            );
        }
    }, [insuranceDetails, hasApprover]);

    const reasons = useMemo(() => {
        let reasonsList = [];
        if (hasApprover && insuranceDetails?.human && !insuranceDetails.human.result) {
            if (!insuranceDetails.human.nameMatches) {
                reasonsList.push({ key: "nameMatches", value: "Incorrect named driver" });
            }
            if (!insuranceDetails.human.registrationMatches) {
                reasonsList.push({ key: "registrationMatches", value: "Incorrect vehicle registration" });
            }
            if (!insuranceDetails.human.datesInRange) {
                reasonsList.push({ key: "datesInRange", value: "Policy dates mismatch" });
            }
            if (!insuranceDetails.human.businessUse) {
                reasonsList.push({ key: "businessUse", value: "The required type(s) not covered" });
            }
        }
        return reasonsList;
    }, [insuranceDetails, hasApprover]);

    useEffect(() => {
        loadInsuranceDetails();
    }, []);

    useEffect(() => {
        if (currentTab === 2 && insuranceDetails) {
            loadHistory();
        }
    }, [currentTab, insuranceDetails]);
    
    function onTabClick(index) {
        if (loading || actionInProgress) {
            return;
        }

        changeCurrentTab(index);
    }
    
    function changeCurrentTab(index) {
        if (index === currentTab) {
            return;
        }

        let clonedTabs = tabs.map(function (t, i) {
            return {
                ...t,
                selected: index === i
            }
        });

        setTabs(clonedTabs);
        setCurrentTab(index);
    }
    
    function loadHistory() {
        setHistoryLoading(true);
        GreyFleetService.getInsuranceCheckHistory(id)
            .then(r => {
                setHistoryData(r.data);
                setHistoryRecordCount(r.totalCount);
                setHistoryLoading(false);
            }
        );
    }

    function loadInsuranceDetails() {
        if (!canAccessPage) {
            return;
        }
        setLoading(true);
        changeCurrentTab(0);
        axios
            .all([
                GreyFleetService.getInsuranceForReview(id),
                GreyFleetService.getInsuranceCoverTypes()
            ])
            .then(
                axios.spread(function (response, coverTypeOptions) {
                    setInsuranceDetails(response || null);
                    setCoverTypes(coverTypeOptions ?? []);
                }),
                () => {
                    NotificationToaster.show(Intent.DANGER, "Could not load insurance details. Please refresh the page.");
                }
            )
            .catch(() => {
                NotificationToaster.show(Intent.DANGER, "Could not load insurance details. Please refresh the page.");
            })
            .finally(() => {
                setLoading(false);
            });
    }

    return (
        <UserAccess perform={requiredAction}
            yes={() => (
                <div className="row">
                    <Breadcrumbs items={breadcrumbs} />
                    <h1>{title}</h1>

                    <div className={classNames("spacer-bottom", { "bp6-skeleton": loading })}>
                        <NotificationInline
                            intent={insuranceDetails?.human?.result ? Intent.SUCCESS : Intent.DANGER}
                            show={!loading && hasApprover}
                            text={notificationText}
                            allowClose={false}
                            isBanner={true}
                            withIcon={false}
                        />
                    </div>

                    {hasApprover && <ShowHide
                        evaluator={insuranceDetails?.human?.result ?? false}
                        show={<Button className="spacer-bottom" disabled={loading || actionInProgress} text="Invalidate Insurance" intent={Intent.DANGER} onClick={() => {setShowInvalidationModal(true)}} />}
                        hide={<Button className="spacer-bottom" disabled={loading || actionInProgress} text="Validate Insurance" intent={Intent.SUCCESS} onClick={() => {setShowValidationModal(true)}} />}
                    />}

                    <div className="spacer-bottom">
                        <Tabs tabs={tabs} onClick={onTabClick} />
                    </div>

                    <ShowHide
                        evaluator={currentTab === 0}
                        show={
                            <Fragment>
                                <GreyFleetInsuranceDetails loading={loading} insuranceDetails={insuranceDetails} coverTypes={coverTypes} />
                                <GreyFleetInsuranceAIResults loading={loading} insuranceDetails={insuranceDetails} />

                                <ShowHide
                                    evaluator={hasApprover}
                                    show={
                                        <div className="spacer-bottom spacer-top">
                                            <h2>Human Validation</h2>
                                            <GridLayout gap={40} numberOfColumns={4} className={classNames({ "bp6-skeleton": loading })}>
                                                <DataDisplayRecord header="Human Results" data={humanResult} />
                                                {!insuranceDetails?.human?.result && <DataDisplayRecord header="Reason(s)" data={reasons.map((r) => <div key={r.key}>{r.value}</div>)} />}
                                            </GridLayout>
                                        </div>
                                    }
                                />
                            </Fragment>
                        }
                    />

                    <ShowHide
                        evaluator={currentTab === 1}
                        show={
                            <div className="spacer-bottom">
                                <h2>Documents</h2>
                                <GreyFleetInsuranceDocument url={insuranceDetails?.document?.url} iframeHeight="100vh" />
                            </div>
                        }
                    />

                    <ShowHide
                        evaluator={currentTab === 2}
                        show={
                            <div className="spacer-bottom">
                                <h2>History</h2>
                                <AuditingTable
                                    hideHeader
                                    key={'gf-insurance-check-history'}
                                    data={historyData}
                                    totalCount={historyRecordCount}
                                    onChange={loadHistory}
                                    tableHeaders={["Edited On", "Edited By", "Change Type", "Change From", "Change To" ]}
                                    noDataMessage={noHistoryDataMessage}
                                    loading={historyLoading}
                                    hideDatesAndPaging={true}
                                />
                            </div>
                        }
                    />

                    <GreyFleetInsuranceValidationModal
                        isOpen={showValidationModal}
                        onClose={() => setShowValidationModal(false)}
                        onSuccess={() => {
                            setShowValidationModal(false);
                            loadInsuranceDetails();
                        }}
                        insuranceDetails={insuranceDetails}
                        actionInProgress={actionInProgress}
                        setActionInProgress={setActionInProgress}
                    />

                    <GreyFleetInsuranceInvalidationModal
                        isOpen={showInvalidationModal}
                        onClose={() => setShowInvalidationModal(false)}
                        onSuccess={() => {
                            setShowInvalidationModal(false);
                            loadInsuranceDetails();
                        }}
                        insuranceDetails={insuranceDetails}
                        actionInProgress={actionInProgress}
                        setActionInProgress={setActionInProgress}
                    />
                </div>
            )}
            no={() => (
                <PageRestricted />
            )}
        />
    );
}

GreyFleetInsuranceValidation.propTypes = {
  breadcrumbs: PropTypes.array.isRequired,
  requiredAction: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired
};